import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { fetchUserProfile } from "../../api/profile";
import { Button } from "../common/Button";
import LoadingSpinner from "../common/Loader";

import { FaFacebookF, FaWhatsapp, FaEnvelope, FaLink } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { FaTelegramPlane } from "react-icons/fa";
import { FaLinkedinIn } from "react-icons/fa";

const ReferralScreen = () => {
  const { user } = useContext(AuthContext);

  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const code = await fetchUserProfile(user.user_id);
        setReferralCode(code);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Generate Referral Link
  const referralType = "Affiliate";
  const encodedReferral = btoa(`${referralType}:${referralCode}`);
  const referralBaseURL =
    "https://portal.investain.com/live-account?ref=";

  const fullReferralLink =
    referralBaseURL + encodeURIComponent(encodedReferral);

  // Copy Link
  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(fullReferralLink);
      alert("Referral link copied successfully!");
    } catch {
      alert("Unable to copy referral link.");
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="w-full bg-white rounded-xl shadow-lg overflow-hidden">

        {/* Header */}
        <div className="bg-[#4d4d4d] text-white p-8 flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold">
              Refer Friends. Earn Rewards.
            </h2>
            <p className="text-gray-300 mt-2">
              Share your referral link and earn rewards whenever your
              friends join Investain.
            </p>
          </div>

          <Button width="w-36">
            How it Works
          </Button>
        </div>

        {/* Referral Link */}
        <div className="p-8">

          <h3 className="text-lg font-semibold mb-4">
            Your Referral Link
          </h3>

          <div className="flex items-center gap-4">
            <input
              type="text"
              value={fullReferralLink}
              readOnly
              className="flex-1 border border-gray-300 rounded-lg px-4 py-3 bg-gray-50 outline-none"
            />

            <Button
              width="w-36"
              onClick={copyReferralLink}
            >
              Copy Link
            </Button>
          </div>

          {/* Social Share */}

          <div className="mt-10">

            <h4 className="font-semibold text-gray-700 mb-5">
              Share via
            </h4>

            <div className="flex items-center justify-between gap-x-16">

              {/* Facebook */}

              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  fullReferralLink
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Facebook"
                className="text-primary transform transition-all duration-300 hover:scale-125 hover:-translate-y-2 hover:rotate-6"
              >
                <FaFacebookF size={34} />
              </a>

              {/* X */}

              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                  fullReferralLink
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                title="X"
                className="text-primary transform transition-all duration-300 hover:scale-125 hover:-translate-y-2 hover:rotate-6"
              >
                <FaXTwitter size={34} />
              </a>

              {/* WhatsApp */}

              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                  fullReferralLink
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
                className="text-primary transform transition-all duration-300 hover:scale-125 hover:-translate-y-2 hover:rotate-6"
              >
                <FaWhatsapp size={38} />
              </a>

              {/* Email */}

              <a
                href={`mailto:?subject=Referral Link&body=Check out this referral link: ${encodeURIComponent(
                  fullReferralLink
                )}`}
                title="Email"
                className="text-primary transform transition-all duration-300 hover:scale-125 hover:-translate-y-2 hover:rotate-6"
              >
                <FaEnvelope size={34} />
              </a>

              {/* Telegram */}
              <a
                href={`https://t.me/share/url?url=${encodeURIComponent(
                  fullReferralLink
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Telegram"
                className="text-primary transform transition-all duration-300 hover:scale-125 hover:-translate-y-2 hover:rotate-6"
              >
                <FaTelegramPlane size={34} />
              </a>

              {/* LinkedIn */}
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                  fullReferralLink
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                title="LinkedIn"
                className="text-primary transform transition-all duration-300 hover:scale-125 hover:-translate-y-2 hover:rotate-6"
              >
                <FaLinkedinIn size={34} />
              </a>

              {/* Copy link */}
              <button
                type="button"
                onClick={copyReferralLink}
                title="Copy Link"
                className="text-primary transform transition-all duration-300 hover:scale-125 hover:-translate-y-2 hover:rotate-6"
              >
                <FaLink size={34} />
              </button>

            </div>

          </div>

        </div>

        {/* Stats (Optional) */}

        {/*
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 p-8 pt-0">

          <div className="bg-gray-50 rounded-lg p-5 text-center">
            <h2 className="text-3xl font-bold">0</h2>
            <p>Total Invited</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5 text-center">
            <h2 className="text-3xl font-bold">0</h2>
            <p>Rewarded Friends</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5 text-center">
            <h2 className="text-3xl font-bold">$0</h2>
            <p>Total Earnings</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5 text-center">
            <h2 className="text-3xl font-bold">$0</h2>
            <p>Rewards Pending</p>
          </div>

        </div>
        */}

      </div>
    </div>
  );
};

export default ReferralScreen;

